// Five architectural silhouettes for the same seven-hex townhall plot. Utility
// props and live inventory are drawn separately so their interaction anchors do
// not move when a settlement's visual phase changes.
import type { TownHallPhase } from '../../../shared/townhall'
import { MeshBuilder } from './mesh'
import { MAT } from './palette3d'
import { barrel, pennant, roof, windowDetail } from './details3d'

const CANVAS = '#ddd0ac'
const CANVAS_LIGHT = '#eee1c2'
const CANVAS_SEAM = '#b5a47e'

/** Outward-facing canvas. The legacy gable primitive winds all faces inward;
 * correct upward roof normals are essential for receiving soft sunlight/shadows.
 * Keep this scoped to the new canvas shelters rather than changing old models. */
export function canvasRoof(mb: MeshBuilder, x: number, y: number, z: number, w: number, h: number, d: number, color = CANVAS): void {
  const ln = [x - w / 2, y, z - d / 2], lf = [x - w / 2, y, z + d / 2]
  const rn = [x + w / 2, y, z - d / 2], rf = [x + w / 2, y, z + d / 2]
  const tn = [x, y + h, z - d / 2], tf = [x, y + h, z + d / 2]
  mb.quad(ln, lf, tf, tn, color, .97)
  mb.quad(rf, rn, tn, tf, color)
  mb.tri(ln[0], ln[1], ln[2], tn[0], tn[1], tn[2], rn[0], rn[1], rn[2], color, .95)
  mb.tri(rf[0], rf[1], rf[2], tf[0], tf[1], tf[2], lf[0], lf[1], lf[2], color, .95)
}

/** A-frame shelter, opening toward the camera-facing south (+Z) side. */
function tent(mb: MeshBuilder, x: number, z: number, w: number, h: number, d: number, color: string): void {
  const front = z + d / 2
  canvasRoof(mb, x, .6, z, w, h, d)
  // Tied-back flaps reveal the dark triangular interior without a stone base.
  mb.tri(x - w * .24, .75, front + .13, x + w * .24, .75, front + .13, x, h * .77, front + .13, MAT.woodDk)
  mb.tri(x - w / 2, .7, front + .26, x - w * .24, .7, front + .26, x - w * .12, h * .53, front + .26, CANVAS_LIGHT)
  mb.tri(x + w * .24, .7, front + .26, x + w / 2, .7, front + .26, x + w * .12, h * .53, front + .26, CANVAS_LIGHT)
  for (const end of [-1, 1]) {
    mb.cyl(x, 0, z + end * (d / 2 + 1.5), .7, h + 2.4, 5, MAT.woodDk)
    mb.beam([x - w / 2, .9, z + end * d / 2], [x, h + .8, z + end * d / 2], .45, CANVAS_SEAM)
    mb.beam([x + w / 2, .9, z + end * d / 2], [x, h + .8, z + end * d / 2], .45, CANVAS_SEAM)
  }
  mb.beam([x, h + 1.6, z - d / 2 - 2.3], [x, h + 1.6, z + d / 2 + 2.3], .85, MAT.woodPale)
  for (const side of [-1, 1]) for (const end of [-1, 1]) {
    const pegX = x + side * (w / 2 + 3.2), pegZ = z + end * (d / 2 + 1)
    mb.cyl(pegX, 0, pegZ, .5, 2.3, 4, MAT.woodDk)
    mb.beam([x + side * w * .25, h * .52 + .6, z + end * d / 2], [pegX, 1.1, pegZ], .22, CANVAS_SEAM)
  }
  // A modest ownership ribbon instead of a monumental tower or flagpole.
  mb.quad([x, h + 1.6, front + 2], [x + 4.4, h + .6, front + 2.2], [x + 3.7, h - 2.3, front + 2.2], [x, h - 1.6, front + 2], color)
}

function bedroll(mb: MeshBuilder, x: number, z: number, color: string): void {
  mb.box(x, .7, z, 5.2, 1.4, 11, MAT.thatchDk)
  mb.box(x, 1.7, z - .7, 4.9, 1.1, 8.8, color)
  mb.gem(x, 2.2, z + 4, 5.3, 3, 3.3, CANVAS_LIGHT, 6)
  for (const dx of [-1.5, 1.5]) mb.box(x + dx, 2.2, z + 4, .4, 2.9, 3.5, MAT.timber)
}

function hearth(mb: MeshBuilder, x: number, z: number, radius = 5.7): void {
  mb.cyl(x, .1, z, radius * .83, .3, 9, '#564d3b')
  for (let i = 0; i < 9; i++) {
    const a = i / 9 * Math.PI * 2
    mb.gem(x + Math.cos(a) * radius, 1.35, z + Math.sin(a) * radius, 2.8, 2.7, 2.4, i % 2 ? MAT.stone : '#bbb4a0', 5)
  }
  mb.box(x, 1.1, z, radius * 1.35, 1.7, 1.7, MAT.timber, .5)
  mb.box(x, 1.8, z, radius * 1.35, 1.7, 1.7, MAT.woodDk, -.7)
  mb.pyramid(x, 2, z, 2.7, 7.3, 5, '#df7e32')
  mb.pyramid(x - .35, 2, z + .5, 1.45, 5.1, 5, '#ffd486')
  mb.pyramid(x + 1.1, 3.1, z - .4, 1.2, 4.6, 4, '#efaa49')
}

function logPile(mb: MeshBuilder, x: number, z: number): void {
  for (let row = 0; row < 2; row++) for (let i = 0; i < 3 - row; i++) {
    const dx = x + i * 2.3 + row * 1.15, y = 1.4 + row * 2.2
    mb.box(dx, y, z, 2.3, 2.5, 11 - (i % 2), MAT.wood, .03 * i)
    mb.box(dx, y, z + 5.6 - (i % 2) * .5, 1.9, 2, .2, MAT.woodPale)
  }
}

function southWindow(mb: MeshBuilder, x: number, y: number, z: number, w: number, h: number): void {
  mb.box(x, y, z, w + 2, h + 2, .9, MAT.timber)
  mb.box(x, y, z + .6, w, h, .5, '#dba968')
  mb.box(x, y, z + 1, .65, h, .45, MAT.woodDk)
  mb.box(x, y, z + 1, w, .65, .45, MAT.woodDk)
  mb.box(x, y - h / 2 - 1.2, z + 1.1, w + 3, 1.1, 2.3, MAT.woodPale)
}

function timberCore(mb: MeshBuilder, fortified: boolean, color: string): void {
  const width = fortified ? 46 : 40, depth = fortified ? 35 : 30
  const base = fortified ? 8 : 2.2, wall = fortified ? 29 : 24, z = -5
  mb.box(0, base / 2, z, width + 4, base, depth + 4, fortified ? MAT.stone : MAT.woodDk)
  mb.box(0, base + wall / 2, z, width, wall, depth, MAT.wood)
  for (let y = base + 3; y < base + wall; y += 4.5) {
    for (const end of [-1, 1]) mb.box(0, y, z + end * (depth / 2 + .12), width + .6, .65, .45, MAT.timber)
  }
  for (const side of [-1, 1]) for (const end of [-1, 1]) mb.box(side * (width / 2 - .8), base + wall / 2, z + end * (depth / 2 - .6), 2.4, wall + 1, 2.4, MAT.timber)
  for (const end of [-1, 1]) {
    mb.box(0, base + wall - 1, z + end * depth / 2, width + 1, 2, 2, MAT.woodDk)
    for (const side of [-1, 1]) mb.beam([side * width * .45, base + 3, z + end * (depth / 2 + .7)], [side * width * .22, base + wall - 3, z + end * (depth / 2 + .7)], 1.2, MAT.woodPale)
  }
  roof(mb, 0, base + wall, z, width + 7, fortified ? 15 : 16, depth + 8, fortified ? MAT.slate : MAT.thatch)
  const front = z + depth / 2
  // Entrance remains centered toward the little courtyard and common fire.
  mb.box(-2, base + 7.5, front + .6, 8.3, 15, 1.5, MAT.woodDk)
  for (const x of [-4.5, -2, .5]) mb.box(x, base + 7.2, front + 1.5, 2, 13.9, .4, MAT.woodPale)
  mb.box(.5, base + 8.2, front + 1.9, .7, .7, .5, MAT.gold)
  southWindow(mb, -13, base + 15, front + .6, 4.7, 6.8)
  southWindow(mb, 11, base + 15, front + .6, 4.7, 6.8)
  windowDetail(mb, -11, base + 14, z - depth / 2 - .6, 4.7, 6.8)
  windowDetail(mb, 11, base + 14, z - depth / 2 - .6, 4.7, 6.8)
  mb.box(-2, base * .45, front + 3, 11, Math.max(1.2, base * .9), 5, fortified ? MAT.stone : MAT.wood)
  mb.box(width * .32, base + wall + 3, z - 5, 4.7, 16, 4.7, fortified ? MAT.stone : MAT.stoneDk)
  mb.box(width * .32, base + wall + 11.4, z - 5, 6, 1.9, 6, MAT.stoneDk)
  mb.box(width * .32, base + wall + 12.4, z - 5, 3.3, .3, 3.3, MAT.coal)
  pennant(mb, -width * .33, base + wall + 5, z + 4, color)
}

function stoneTower(mb: MeshBuilder, x: number, z: number, height: number): void {
  mb.box(x, 2.3, z, 13.5, 4.6, 13.5, MAT.stoneDk)
  mb.box(x, height / 2 + 3, z, 11.5, height, 11.5, MAT.stone)
  for (const y of [14, 29, height - 5]) mb.box(x, y, z, 12, 1.3, 12, '#b9b8aa')
  mb.box(x, height + 4.2, z, 14, 2.4, 14, MAT.stoneDk)
  mb.box(x, height + 5.7, z, 12.5, 1, 12.5, MAT.slate)
  for (const side of [-1, 1]) for (const end of [-1, 1]) mb.box(x + side * 5, height + 8.2, z + end * 5, 4, 5, 4, '#bbb9ab')
  mb.box(x, height * .59, z + 5.85, 1.7, 8.5, .4, MAT.coal)
  mb.box(x, height * .59, z - 5.85, 1.7, 8.5, .4, MAT.coal)
}

export function townHallArchitecture(mb: MeshBuilder, phase: TownHallPhase, color: string): void {
  if (phase === 1) {
    tent(mb, -5, -8, 24, 17, 20, color)
    hearth(mb, -5, 14, 4.6)
    bedroll(mb, -21, 11, '#8d9b79')
    bedroll(mb, -15, 11, '#b7a17b')
    mb.box(4, 2, 10, 7, 3, 3, MAT.wood, -.15) // a simple seat by the fire
    return
  }
  if (phase === 2) {
    tent(mb, -7, -8, 30, 22, 27, color)
    tent(mb, 17, -11, 15, 13, 18, '#b6c1a3')
    hearth(mb, -9, 16, 5)
    bedroll(mb, -24, 14, '#8d9b79')
    bedroll(mb, -18, 14, '#b7a17b')
    mb.box(-2, 3.5, 18, 10, 2, 3.5, MAT.woodPale)
    for (const x of [-5, 1]) mb.box(x, 1.8, 18, 1.2, 3.6, 2.6, MAT.woodDk)
    logPile(mb, 23, -23)
    barrel(mb, -27, 0, -20, 2.7, 6)
    return
  }
  if (phase === 3) {
    timberCore(mb, false, color)
    hearth(mb, -18, 19, 4.5)
    mb.box(-29, 6, 2, 7, 12, 18, MAT.wood)
    mb.gable(-29, 12, 2, 9, 5, 20, MAT.thatch)
    logPile(mb, -28, -18)
    barrel(mb, 27, 0, -15)
    return
  }
  if (phase === 4) {
    timberCore(mb, true, color)
    // Buttressed stone footings and stout timber corners signal fortification
    // without prematurely turning the whole settlement into a stone castle.
    for (const side of [-1, 1]) for (const z of [-18, 8]) {
      mb.box(side * 25, 9, z, 5, 18, 5, MAT.stoneDk)
      mb.box(side * 25, 18.7, z, 6, 1.4, 6, MAT.stone)
    }
    for (const side of [-1, 1]) {
      for (let i = 0; i < 5; i++) {
        const z = -20 + i * 6.3
        mb.box(side * 29, 13, z, 2.8, 26, 3.2, MAT.timber)
        mb.pyramid(side * 29, 26, z, 2.2, 3.2, 4, MAT.woodPale, Math.PI / 4)
      }
      mb.box(side * 29, 12, -7.4, 2, 2, 31, MAT.wood)
    }
    hearth(mb, -17, 22, 4.1)
    mb.box(-20, 35, 13.5, 7, 12, .7, color)
    mb.tri(-23.5, 29, 13.9, -20, 26, 13.9, -16.5, 29, 13.9, color)
    return
  }
  // Stone keep: compact masonry mass, recessed central gate, square corner
  // towers and a stepped crenellated roofline. The east workbench stays outside.
  mb.box(0, 3, -3, 54, 6, 47, MAT.stoneDk)
  mb.box(0, 25, -3, 49, 44, 42, '#aaa99d')
  for (const y of [9, 20, 31, 43]) {
    mb.box(0, y, 18.2, 49.5, .9, .7, '#c1bdac')
    mb.box(0, y, -24.2, 49.5, .9, .7, '#c1bdac')
  }
  for (let row = 0; row < 4; row++) for (let i = 0; i < 6; i++) {
    const x = -21 + i * 8 + (row % 2) * 3
    if (Math.abs(x) < 23) mb.box(x, 14 + row * 10, 18.7, .65, 8, .3, MAT.stoneDk)
  }
  mb.box(0, 48, -3, 53, 3, 46, MAT.stoneDk)
  mb.box(0, 50, -3, 47, 1.5, 40, MAT.slate)
  for (const end of [-1, 1]) for (let i = 0; i < 7; i++) mb.box(-22 + i * 7.3, 53, -3 + end * 21, 4.5, 7, 4, MAT.stone)
  for (const side of [-1, 1]) for (let i = 0; i < 5; i++) mb.box(side * 24.5, 53, -18 + i * 7.5, 4, 7, 4.5, MAT.stone)
  stoneTower(mb, -23, -22, 51)
  stoneTower(mb, 23, -22, 51)
  // Raised inner keep gives the final stage an unmistakable, tall silhouette.
  mb.box(0, 53, -10, 20, 18, 18, MAT.stone)
  mb.box(0, 63, -10, 23, 2.5, 21, MAT.stoneDk)
  mb.box(0, 64.6, -10, 19, 1, 17, MAT.slate)
  for (const x of [-9, 0, 9]) for (const z of [-18.5, -1.5]) mb.box(x, 67, z, 4, 5, 4, '#c3c0b1')
  mb.box(-4, 16, 18.8, 13, 23, 1.3, MAT.stoneDk)
  mb.box(-4, 15, 19.6, 10, 20, .8, MAT.woodDk)
  for (const x of [-7.8, -5.9, -4, -2.1, -.2]) mb.box(x, 15, 20.2, 1.3, 19.5, .45, MAT.wood)
  for (const y of [9, 17, 23]) mb.box(-4, y, 20.6, 10, .9, .5, MAT.metalDk)
  mb.box(-4, 4.2, 22.3, 16, 2.4, 7, MAT.stone)
  southWindow(mb, -17, 34, 18.9, 3.8, 8)
  southWindow(mb, 13, 34, 18.9, 3.8, 8)
  for (const x of [-16, 14]) {
    mb.box(x, 39.5, 20, 6.5, 1.2, 1.3, MAT.woodDk)
    mb.box(x, 31.5, 20.2, 5.8, 14, .65, color)
    mb.tri(x - 2.9, 24.5, 20.6, x, 21.4, 20.6, x + 2.9, 24.5, 20.6, color)
  }
  pennant(mb, 0, 70, -10, color)
  hearth(mb, -20, 24, 3.6)
}
